import React, { useEffect, useState } from "react";

const COVER_URL = "https://www.bbc.co.uk/weather";
const SEEN_KEY = "tanursery_quickexit_seen_v1";

function performQuickExit() {
  try {
    // 1) Mute any media playing on the page (audio/video/screen-reader-fed media)
    try {
      document.querySelectorAll("audio, video").forEach((el) => {
        try { el.pause(); el.muted = true; el.currentTime = 0; } catch (e) { /* noop */ }
      });
    } catch (e) { /* noop */ }

    // 2) Blank the page instantly so nothing identifiable is on screen
    //    during the redirect — covers the half-second before navigation lands.
    try {
      const blank = document.createElement("div");
      blank.setAttribute("aria-hidden", "true");
      blank.style.cssText =
        "position:fixed;inset:0;background:#FAF7F2;z-index:2147483647;display:flex;align-items:center;justify-content:center;font-family:sans-serif;color:#5C6B64;font-size:14px;";
      blank.textContent = "Loading…";
      document.body.appendChild(blank);
      // Clear the visible title so tab-preview doesn't reveal the site
      try { document.title = "Weather - BBC"; } catch (e) { /* noop */ }
    } catch (e) { /* noop */ }

    // 3) Wipe the current history entry so back-button can't return here
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", "/");
    }

    // 4) Replace current tab with cover site (no back-button trail)
    window.location.replace(COVER_URL);

    // 5) Best-effort attempt to close the tab
    setTimeout(() => {
      try { window.open("", "_self"); window.close(); } catch (e) { /* noop */ }
    }, 150);
  } catch (e) {
    window.location.href = COVER_URL;
  }
}

export default function QuickExit() {
  const [showBanner, setShowBanner] = useState(false);
  const [escCount, setEscCount] = useState(0);

  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(SEEN_KEY);
      if (!seen) setShowBanner(true);
    } catch (e) { /* localStorage blocked — silently skip */ }
  }, []);

  useEffect(() => {
    let timer;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setEscCount((c) => {
          const next = c + 1;
          if (next >= 2) {
            performQuickExit();
            return 0;
          }
          clearTimeout(timer);
          timer = setTimeout(() => setEscCount(0), 800);
          return next;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(timer);
    };
  }, []);

  const dismissBanner = () => {
    try { window.localStorage.setItem(SEEN_KEY, "1"); } catch (e) { /* noop */ }
    setShowBanner(false);
  };

  return (
    <>
      {/* Fixed exit button — always visible */}
      <button
        type="button"
        onClick={performQuickExit}
        data-testid="quick-exit-btn"
        aria-label="Quick exit — leave this site immediately"
        title="Quick exit — or press Esc twice"
        className="fixed top-4 right-4 z-[60] inline-flex items-center gap-2 px-4 py-2.5 bg-[#8B1E1E] text-white text-sm font-medium tracking-wide rounded-sm shadow-lg hover:bg-[#6f1616] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#8B1E1E] focus:ring-offset-brand-bg transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
        Quick Exit
      </button>

      {/* First-visit info banner */}
      {showBanner && (
        <div
          role="dialog"
          aria-label="Safety information"
          data-testid="quick-exit-banner"
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-[55] bg-brand-forest text-brand-bg border border-brand-bg/20 rounded-sm shadow-2xl p-5"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-brand-terracotta mb-3">
            Your safety
          </p>
          <p className="text-sm leading-relaxed text-brand-bg/90">
            If you need to leave this site quickly, tap the{" "}
            <strong className="text-brand-bg">Quick Exit</strong> button (top
            right) or press <kbd className="px-1.5 py-0.5 bg-brand-bg/15 rounded-sm font-mono text-xs">Esc</kbd>{" "}
            twice. You will be sent to the BBC Weather page and this tab's
            history will be replaced.
          </p>
          <p className="text-xs leading-relaxed text-brand-bg/70 mt-3">
            <strong className="text-brand-bg/90">For deepest safety:</strong>{" "}
            use a private / incognito window (Ctrl + Shift + N on Chrome,
            Cmd + Shift + N on Safari) — nothing you do will be saved to this
            device's browser history. We also recommend clearing your history
            after visiting.
          </p>
          <p className="text-xs leading-relaxed text-brand-bg/60 mt-3">
            In an emergency, call 999 (UK) or your local emergency
            number.
          </p>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={dismissBanner}
              data-testid="quick-exit-banner-dismiss"
              className="px-4 py-2 bg-brand-terracotta text-brand-bg text-xs uppercase tracking-wide rounded-sm hover:bg-brand-terracottaDark transition-colors"
            >
              I understand
            </button>
            <button
              type="button"
              onClick={performQuickExit}
              data-testid="quick-exit-banner-try"
              className="px-4 py-2 border border-brand-bg/30 text-brand-bg text-xs uppercase tracking-wide rounded-sm hover:bg-brand-bg/10 transition-colors"
            >
              Try it now
            </button>
          </div>
        </div>
      )}
    </>
  );
}
